import { Link } from 'react-router-dom';
import PageMeta from '../../components/SEO/PageMeta';
import { FORMAT_LABELS, getAllConversionRoutes } from '../../config/conversions';
import { IMAGE_FORMATS } from '../../types/media';
import { SITE_NAME, SITE_TAGLINE } from '../../config/site';
import styles from './HomePage.module.css';

const routes = getAllConversionRoutes();

const featureCards = [
  {
    title: 'Image & media converters',
    description:
      'Convert JPEG, PNG, WebP, SVG, GIF, BMP, TIFF, and ICO files with dedicated SEO-friendly pages.',
    to: '/image-converters',
    action: 'Explore converters',
    visual: 'media',
    stat: `${routes.length} routes`,
  },
  {
    title: 'String tools',
    description:
      'Change case, count characters and words, clean whitespace, reverse text, and compare strings.',
    to: '/string-tools',
    action: 'Open string tools',
    visual: 'text',
    stat: '13 tools',
  },
  {
    title: 'Array utilities',
    description:
      'Inspect, sort, dedupe, flatten, chunk, compare, and convert arrays for quick developer workflows.',
    to: '/array-tools',
    action: 'Open array tools',
    visual: 'array',
    stat: '9 tools',
  },
  {
    title: 'JSON & object tools',
    description:
      'Format JSON, minify payloads, extract keys and values, flatten objects, compare, and merge data.',
    to: '/javascript-object-tools',
    action: 'Open object tools',
    visual: 'object',
    stat: '10 tools',
  },
  {
    title: 'JavaScript workspace',
    description:
      'Use the browser-safe terminal and online editor for snippets, console output, history, and experiments.',
    to: '/online-javascript-editor',
    action: 'Open JS editor',
    visual: 'code',
    stat: 'Editor + terminal',
  },
  {
    title: 'Time calculators',
    description:
      'Compare two times, view live timezone clocks, and total multiple intervals from one clean tool.',
    to: '/time-tools',
    action: 'Open time tools',
    visual: 'time',
    stat: 'Timezone aware',
  },
];

const quickLinks = [
  { label: 'String compare', to: '/string-tools/text-compare' },
  { label: 'JSON formatter', to: '/javascript-object-tools/json-formatter' },
  { label: 'Array difference', to: '/array-tools/array-difference' },
  { label: 'JS terminal', to: '/javascript-terminal' },
  { label: 'Time difference', to: '/time-tools' },
  { label: 'Blog guides', to: '/blogs' },
];

export default function HomePage() {
  return (
    <>
      <PageMeta
        title="Free Online Tools for Media, Text, JSON, JavaScript & Time"
        description={`${SITE_TAGLINE}. Fast browser-friendly tools for image conversion, string cleanup, array utilities, JSON formatting, JavaScript snippets, and time calculations.`}
        path="/"
      />

      <section className={styles.hero}>
        <div className={styles.heroCopy}>
          <p className={styles.eyebrow}>All-in-one online toolkit</p>
          <h1>Fast free tools for files, text, code, data, and time.</h1>
          <p>
            {SITE_NAME} brings everyday browser utilities into one polished workspace: media
            converters, string formatters, JSON helpers, array tools, JavaScript sandboxes, time
            calculators, and practical guides.
          </p>
          <div className={styles.heroActions}>
            <a href="#tools" className={styles.primaryAction}>
              Explore tools
            </a>
            <Link to="/blogs" className={styles.secondaryAction}>
              Read guides
            </Link>
          </div>
        </div>
        <div className={styles.heroVisual} aria-label="Animated toolkit preview">
          <div className={styles.visualWindow}>
            <span className={styles.windowDot} />
            <span className={styles.windowDot} />
            <span className={styles.windowDot} />
          </div>
          <div className={styles.visualStack}>
            <span>PNG → WebP</span>
            <span>JSON.format()</span>
            <span>UTC +05:30</span>
          </div>
          <div className={styles.visualPulse} />
        </div>
      </section>

      <section className={styles.quickLinks} aria-label="Popular tools">
        {quickLinks.map((item) => (
          <Link key={item.to} to={item.to}>
            {item.label}
          </Link>
        ))}
      </section>

      <section id="tools" className={styles.featureSection} aria-labelledby="tools-heading">
        <div className={styles.sectionHeader}>
          <p className={styles.eyebrow}>Feature hub</p>
          <h2 id="tools-heading">Everything you can do here</h2>
          <p>Choose a focused tool category and jump into a ready-to-use workflow.</p>
        </div>

        <div className={styles.featureGrid}>
          {featureCards.map((feature) => (
            <Link key={feature.title} to={feature.to} className={styles.featureCard}>
              <FeatureVisual type={feature.visual} />
              <span className={styles.featureStat}>{feature.stat}</span>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
              <span className={styles.cardAction}>{feature.action}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className={styles.trustSection} aria-label="Why use SaveItPro Tools">
        <article>
          <strong>No install</strong>
          <span>Open a tool and work directly in the browser.</span>
        </article>
        <article>
          <strong>Focused pages</strong>
          <span>Each utility has a clear route for sharing and search.</span>
        </article>
        <article>
          <strong>Creator friendly</strong>
          <span>Helpful for content, development, cleanup, testing, and quick calculations.</span>
        </article>
      </section>

      <section className={styles.formats} aria-labelledby="formats-heading">
        <div className={styles.sectionHeader}>
          <p className={styles.eyebrow}>Media converter</p>
          <h2 id="formats-heading">Supported image formats</h2>
        </div>
        <ul className={styles.formatList}>
          {IMAGE_FORMATS.map((format) => (
            <li key={format}>
              <span className={styles.formatChip}>{FORMAT_LABELS[format]}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.gridSection} aria-labelledby="converters-heading">
        <div className={styles.sectionHeader}>
          <p className={styles.eyebrow}>Popular conversion routes</p>
          <h2 id="converters-heading">Image converters</h2>
          <p>Start with common routes, or use any supported format pair.</p>
          <Link to="/image-converters" className={styles.viewAllLink}>
            View all {routes.length} converters
          </Link>
        </div>
        <div className={styles.grid}>
          {routes.slice(0, 18).map(({ slug, from, to }) => (
            <Link key={slug} to={`/media/${slug}`} className={styles.card}>
              <span className={styles.cardFrom}>{FORMAT_LABELS[from]}</span>
              <span className={styles.cardArrow} aria-hidden>
                →
              </span>
              <span className={styles.cardTo}>{FORMAT_LABELS[to]}</span>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

function FeatureVisual({ type }: { type: string }) {
  return (
    <span className={`${styles.featureVisual} ${styles[`${type}Visual`]}`} aria-hidden>
      <span />
      <span />
      <span />
    </span>
  );
}
